import React from 'react';
import { TrendingUp, TrendingDown, PieChart, DollarSign } from 'lucide-react';

const Portfolio = () => {
  const portfolioSummary = {
    totalInvestment: 1200000,
    currentValue: 1545000,
    totalReturns: 345000,
    returnsPercent: 28.75,
    dayChange: 12500,
    dayChangePercent: 0.82
  };

  const holdings = [
    {
      id: 1,
      name: 'Reliance Industries Ltd',
      symbol: 'RELIANCE',
      quantity: 50,
      avgPrice: 2450,
      currentPrice: 2568,
      investment: 122500,
      currentValue: 128400,
      returns: 5900,
      returnsPercent: 4.82,
      weight: 8.3
    },
    {
      id: 2,
      name: 'Tata Consultancy Services',
      symbol: 'TCS',
      quantity: 30,
      avgPrice: 3890,
      currentPrice: 3960,
      investment: 116700,
      currentValue: 118800,
      returns: 2100,
      returnsPercent: 1.80,
      weight: 7.7
    },
    {
      id: 3,
      name: 'HDFC Bank Ltd',
      symbol: 'HDFCBANK',
      quantity: 100,
      avgPrice: 1680,
      currentPrice: 1672,
      investment: 168000,
      currentValue: 167200,
      returns: -800,
      returnsPercent: -0.48,
      weight: 10.8
    },
    {
      id: 4,
      name: 'Asian Paints Ltd',
      symbol: 'ASIANPAINT',
      quantity: 40,
      avgPrice: 2890,
      currentPrice: 3120,
      investment: 115600,
      currentValue: 124800,
      returns: 9200,
      returnsPercent: 7.96,
      weight: 8.1
    },
    {
      id: 5,
      name: 'ICICI Bank Ltd',
      symbol: 'ICICIBANK',
      quantity: 80,
      avgPrice: 1050,
      currentPrice: 1120,
      investment: 84000,
      currentValue: 89600,
      returns: 5600,
      returnsPercent: 6.67,
      weight: 5.8
    }
  ];

  const sectors = [
    { name: 'Banking', value: 35, color: '#3B82F6' },
    { name: 'IT', value: 25, color: '#10B981' },
    { name: 'Energy', value: 20, color: '#F59E0B' },
    { name: 'Consumer Goods', value: 15, color: '#8B5CF6' },
    { name: 'Others', value: 5, color: '#6B7280' }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">My Portfolio</h2>
        <p className="text-gray-600">Track your investments and performance</p>
      </div>

      {/* Portfolio Summary */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-blue-100 rounded-lg">
              <DollarSign className="text-blue-600" size={24} />
            </div>
            <p className="text-sm text-gray-600 font-medium">Total Investment</p>
          </div>
          <p className="text-2xl font-bold text-gray-900">₹{(portfolioSummary.totalInvestment / 100000).toFixed(2)}L</p>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-green-100 rounded-lg">
              <TrendingUp className="text-green-600" size={24} />
            </div>
            <p className="text-sm text-gray-600 font-medium">Current Value</p>
          </div>
          <p className="text-2xl font-bold text-gray-900">₹{(portfolioSummary.currentValue / 100000).toFixed(2)}L</p>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <PieChart className="text-yellow-600" size={24} />
            </div>
            <p className="text-sm text-gray-600 font-medium">Total Returns</p>
          </div>
          <p className="text-2xl font-bold text-green-600">
            +₹{(portfolioSummary.totalReturns / 100000).toFixed(2)}L
          </p>
          <p className="text-sm text-green-600 font-semibold">+{portfolioSummary.returnsPercent}%</p>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-purple-100 rounded-lg">
              <TrendingUp className="text-purple-600" size={24} />
            </div>
            <p className="text-sm text-gray-600 font-medium">Day's Change</p>
          </div>
          <p className="text-2xl font-bold text-green-600">
            +₹{(portfolioSummary.dayChange / 1000).toFixed(2)}K
          </p>
          <p className="text-sm text-green-600 font-semibold">+{portfolioSummary.dayChangePercent}%</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Holdings Table */}
        <div className="lg:col-span-2 bg-white rounded-lg shadow overflow-hidden">
          <div className="p-6 border-b">
            <h3 className="text-xl font-bold text-gray-900">Holdings</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase">Stock</th>
                  <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase">Qty</th>
                  <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase">Avg Price</th>
                  <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase">Current</th>
                  <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase">Investment</th>
                  <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase">Returns</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {holdings.map((holding) => (
                  <tr key={holding.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div>
                        <p className="font-semibold text-gray-900 text-sm">{holding.name}</p>
                        <p className="text-xs text-gray-500">{holding.symbol}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">{holding.quantity}</td>
                    <td className="px-6 py-4 text-sm text-gray-900">₹{holding.avgPrice}</td>
                    <td className="px-6 py-4 text-sm font-semibold text-gray-900">₹{holding.currentPrice}</td>
                    <td className="px-6 py-4 text-sm text-gray-900">₹{(holding.investment / 1000).toFixed(1)}K</td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-1">
                        {holding.returns >= 0 ? (
                          <TrendingUp className="text-green-600" size={14} />
                        ) : (
                          <TrendingDown className="text-red-600" size={14} />
                        )}
                        <span className={`text-sm font-semibold ${
                          holding.returns >= 0 ? 'text-green-600' : 'text-red-600'
                        }`}>
                          {holding.returns >= 0 ? '+' : ''}{holding.returnsPercent}%
                        </span>
                      </div>
                      <p className={`text-xs ${
                        holding.returns >= 0 ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {holding.returns >= 0 ? '+' : ''}₹{holding.returns}
                      </p>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Sector Allocation */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-6">Sector Allocation</h3>
          <div className="space-y-4">
            {sectors.map((sector, index) => (
              <div key={index}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">{sector.name}</span>
                  <span className="text-sm font-bold text-gray-900">{sector.value}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="h-2 rounded-full transition-all"
                    style={{ width: `${sector.value}%`, backgroundColor: sector.color }}
                  />
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8 p-4 bg-blue-50 rounded-lg">
            <p className="text-sm text-blue-900 font-semibold mb-1">Portfolio Diversification</p>
            <p className="text-xs text-blue-700">Your portfolio is well diversified across {sectors.length} sectors</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Portfolio;
